import {omneticApi} from '@dms/api/base';

import {
  AssignSaleVehiclePhotoTypeApiArg,
  SaleVehicleAlbumPhotoGuide,
  SaleVehicleAlbumPhotoGuideApiArg,
  SaleVehicleAlbumPhotoGuideSchema,
  UnassignSaleVehiclePhotoFromGuideApiArg,
  UpdateSaleVehicleAlbumPhotoGuideLayoutApiArg,
} from './types';

const getPhotoGuideUrl = (queryArg: SaleVehicleAlbumPhotoGuideApiArg) =>
  `/dms/v1/sale-vehicle/${queryArg.saleVehicleId}/sale-vehicle-album/${queryArg.saleVehicleAlbumId}/photo-guide`;

// Every layout mutation rewrites the album's photo weights on the backend, so the album photo
// list and the cover photo are refetched together with the layout. The layout also provides the
// album photos tag: any existing photo mutation (upload, delete, move, copy) then refetches it.
const getPhotoGuideInvalidationTags = (queryArg: SaleVehicleAlbumPhotoGuideApiArg) => [
  {type: 'SaleVehicleAlbumPhotoGuide' as const, id: queryArg.saleVehicleAlbumId},
  {type: 'SaleVehicleAlbumPhotos' as const, id: queryArg.saleVehicleAlbumId},
  'SaleVehicleCoverPhoto' as const,
  'SaleVehicleAlbums' as const,
];

export const saleVehiclePhotoGuideApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getSaleVehicleAlbumPhotoGuide: build.query<
      SaleVehicleAlbumPhotoGuide,
      SaleVehicleAlbumPhotoGuideApiArg
    >({
      query: (queryArg) => ({url: getPhotoGuideUrl(queryArg)}),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SaleVehicleAlbumPhotoGuide', id: queryArg.saleVehicleAlbumId},
        {type: 'SaleVehicleAlbumPhotos', id: queryArg.saleVehicleAlbumId},
      ],
      extraOptions: {responseSchema: SaleVehicleAlbumPhotoGuideSchema},
    }),
    assignSaleVehiclePhotoType: build.mutation<void, AssignSaleVehiclePhotoTypeApiArg>({
      query: (queryArg) => ({
        url: `${getPhotoGuideUrl(queryArg)}/assign`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: (_result, _error, queryArg) => getPhotoGuideInvalidationTags(queryArg),
    }),
    unassignSaleVehiclePhotoFromGuide: build.mutation<
      void,
      UnassignSaleVehiclePhotoFromGuideApiArg
    >({
      query: (queryArg) => ({
        url: `${getPhotoGuideUrl(queryArg)}/unassign`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: (_result, _error, queryArg) => getPhotoGuideInvalidationTags(queryArg),
    }),
    updateSaleVehicleAlbumPhotoGuideLayout: build.mutation<
      void,
      UpdateSaleVehicleAlbumPhotoGuideLayoutApiArg
    >({
      query: (queryArg) => ({
        url: `${getPhotoGuideUrl(queryArg)}/layout`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: (_result, _error, queryArg) => getPhotoGuideInvalidationTags(queryArg),
    }),
    arrangeSaleVehicleAlbumByPhotoGuide: build.mutation<void, SaleVehicleAlbumPhotoGuideApiArg>({
      query: (queryArg) => ({
        url: `${getPhotoGuideUrl(queryArg)}/arrange`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, queryArg) => getPhotoGuideInvalidationTags(queryArg),
    }),
  }),
});

export const {
  useGetSaleVehicleAlbumPhotoGuideQuery,
  useAssignSaleVehiclePhotoTypeMutation,
  useUnassignSaleVehiclePhotoFromGuideMutation,
  useUpdateSaleVehicleAlbumPhotoGuideLayoutMutation,
  useArrangeSaleVehicleAlbumByPhotoGuideMutation,
} = saleVehiclePhotoGuideApi;
